/**
 * Yield control back to the main thread between long init tasks.
 *
 * @returns {Promise<void>}
 */
export function yieldToMain() {
    if ('scheduler' in window && typeof window.scheduler.yield === 'function') {
        return window.scheduler.yield();
    }

    return new Promise((resolve) => {
        setTimeout(resolve, 0);
    });
}

/**
 * Run async tasks sequentially, yielding between each one.
 *
 * @param {Array<() => Promise<unknown>|unknown>} tasks
 * @returns {Promise<void>}
 */
export async function runChunkedTasks(tasks) {
    for (const task of tasks) {
        await task();
        await yieldToMain();
    }
}

/**
 * Schedule work after the page is likely interactive.
 *
 * @param {() => void} callback
 * @param {{ timeout?: number }} options
 */
export function runWhenIdle(callback, { timeout = 2000 } = {}) {
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout });
        return;
    }

    setTimeout(callback, 0);
}

/**
 * Schedule work after window load (post-TTI friendly for analytics).
 *
 * @param {() => void} callback
 */
export function runAfterLoad(callback) {
    if (document.readyState === 'complete') {
        runWhenIdle(callback, { timeout: 4000 });
        return;
    }

    window.addEventListener('load', () => {
        runWhenIdle(callback, { timeout: 4000 });
    }, { once: true });
}

/**
 * Schedule work once DOMContentLoaded has fired.
 *
 * @param {() => void} callback
 */
export function runWhenDomReady(callback) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback, { once: true });
        return;
    }

    callback();
}
